import React from "react";
import { createRoot, Root } from "react-dom/client";
import { MainUI } from "./ui/MainUI";
import { ScreenChangeEvent } from "./ui/events/ScreenEvent";
import { AppScreen } from "./AppScreen";
import { CustomEventDispatcher } from "./events/CustomEventDispatcher";
import { QuizQuestionManager } from "./modules/quiz/QuizQuestionManager";
import { AppMode } from "./AppMode";
import { Links } from "./ui/Links";
import { Terminal } from "./Terminal";
import { Reporting } from "./api/Reporting";

export class App extends CustomEventDispatcher
{
    private static instance: App;
    private _quizQuestionManager: QuizQuestionManager;
    private _iframeUrl: string = "";
    private _appMode: AppMode = AppMode.Departures;
    private _terminal: Terminal = null;
    private _links: Links;
    private _desktopMode: boolean = false;
    private _reporting: Reporting;

    constructor()
    {
        super();

        App.instance = this;

        window.addEventListener("resize", this.onResize.bind(this));

        this._reporting = new Reporting("https://hzxr1tltzl.execute-api.eu-west-2.amazonaws.com");
        this.render();
    }

    private onResize(): void 
    {
        if (this.isDesktopBrowser())
        {
            this.showDesktopMode(true);
        }
        else 
        {
            if (window.innerWidth > window.innerHeight)
            {
                this.showOrientation(true);
            }
            else 
            {
                this.showOrientation(false);
            }

            this.showDesktopMode(false);
        }
    }

    private isDesktopBrowser(): boolean
    {
        try
        {
            // Check for user agent
            var userAgent = navigator.userAgent.toLowerCase();
            var isMobile = /mobile|android|iphone|ipad|iemobile|wpdesktop/i.test(userAgent);

            // Check for touch support
            // @ts-ignore
            var isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);

            // Combine the conditions to determine if it's a desktop browser
            return !isMobile && !isTouchDevice;
        }
        catch
        {
            return false;
        }
    }

    private showDesktopMode(desktopOnly: boolean): void 
    {
        if (desktopOnly)
        {
            document.body.classList.add("desktop-mode");
        }
        else 
        {
            document.body.classList.remove("desktop-mode");
        }
    }

    private showOrientation(portraitOnly: boolean): void 
    {
        if (portraitOnly)
        {
            document.body.classList.add("orientation-message");
        }
        else 
        {
            document.body.classList.remove("orientation-message");
        }
    }

    public async onUIReady(): Promise<void>
    {
        this.setAppmode();
        this._quizQuestionManager = new QuizQuestionManager(this._appMode);
        this.changeScreen(AppScreen.Landing);
        this.onResize();
        this.sendReporting();
    }

    private sendReporting(): void 
    {
        try
        {
            this._reporting.save({
                path: window.location.pathname,
                terminal: this._terminal,
                appMode: AppMode[this._appMode],
                mobile: this.isDesktopMode === false
            });
        }
        catch (e)
        {
        }
    }

    private setAppmode(): void 
    {
        let pathNames = window.location.pathname.split("/");

        if (pathNames.indexOf("arrivals") > -1)
        {
            this._appMode = AppMode.Arrivals;
        }
        else 
        {
            this._appMode = AppMode.Departures;
        }

        if (pathNames.indexOf(Terminal.T2) > -1)
        {
            this._terminal = Terminal.T2;
        }
        else if (pathNames.indexOf(Terminal.T3) > -1)
        {
            this._terminal = Terminal.T3;
        }
        else if (pathNames.indexOf(Terminal.T4) > -1)
        {
            this._terminal = Terminal.T4;
        }
        else if (pathNames.indexOf(Terminal.T5) > -1)
        {
            this._terminal = Terminal.T5;
        }

        console.log("Terminal", this._terminal, "AppMode", AppMode[this._appMode]);
        this._links = new Links(this.appMode, this._terminal);
    }

    public changeScreen(screen: AppScreen): void 
    {
        document.body.className = "";
        document.body.classList.add(`screen`, `screen-${AppScreen[screen].toLowerCase()}`);
        this.dispatchEvent(new ScreenChangeEvent(ScreenChangeEvent.SCREEN_CHANGED, screen));
    }

    public loadIframeUrl(url: string): void 
    {
        App.I.changeScreen(AppScreen.ParentZoneIframe);
        this._iframeUrl = url;
    }

    public openPageInNewTab(url: string): void
    {
        window.open(url, "_blank");
    }

    private render(): void
    {
        const root: Root = createRoot(document.getElementById("root") || document.createElement("div"));
        root.render(React.createElement(MainUI, { onUIReadyCB: this.onUIReady.bind(this) }, null));
    }

    public get iframeUrl(): string { return this._iframeUrl; }
    public get quizQuestionManager(): QuizQuestionManager { return this._quizQuestionManager; }

    public get appLinks(): Links { return this._links; }
    public get appMode(): AppMode { return this._appMode; }
    public get isDesktopMode(): boolean { return this._desktopMode; }
    public static get I(): App { return App.instance; }

}